import { Controller } from "stimulus";
import { maybeConnectBridge } from "utils/hotwire";


/** 🚨🚨🚨🚨 WARNING: This controller has a bridge component counterpart. 🚨🚨🚨🚨
 * 
 * This means that the functionality provided below is handled natively in the mobile apps.
 * Ensure that any changes to this controller are also reflected in the bridge component counterpart, and
 * tested in both Android and iOS.
 * 
 * You can find the bridge component counterpart in:
 * app/components/media/bridge/playback_controls_controller.js
 */

export default class PlaybackControlsController extends Controller {
  static targets = [
    "hideable",
    "progressBar",
    "currentTime",
    "duration",
    "muted",
    "unmuted",
    "airplay",
    "playback0_9",
    "playback1",
    "playback1_1",
    "playback1_5",
    "playback2",
  ]
  static values = {
    itemId: String,
    itemType: String,
    duration: Number
  }

  connect() {
    maybeConnectBridge(this.element, "play-button");

    window.addEventListener("supercast-timeupdate", this.onTimeUpdate.bind(this));
    window.addEventListener("supercast-mute-change", this.onMuteChange.bind(this));
    window.addEventListener("supercast-rate-change", this.onRateChange.bind(this));
  }

  onTimeUpdate(event) {
    if (event.detail.itemId == this.itemIdValue && event.detail.itemType == this.itemTypeValue) {
      this.show();
      this.updateProgress(event);
    } else {
      this.hide();
    }
  }

  togglePlaybackRate() {
    this.getMiniPlayer().togglePlaybackRate();
  }

  jumpBack() {
    this.getMiniPlayer().jumpBack();
  }

  jumpForward() {
    this.getMiniPlayer().jumpForward();
  }

  toggleMute() {
    this.getMiniPlayer().toggleMute();
  }

  showAirplay() {
    this.getMiniPlayer().showAirplay();
  }

  onMuteChange(event) {
    this.mutedTarget.hidden = !event.detail.muted;
    this.unmutedTarget.hidden = event.detail.muted;
  }

  onRateChange(event) {
    const rate = event.detail.playbackRate;
    this.playback0_9Target.hidden = rate !== 0.9;
    this.playback1Target.hidden = rate !== 1;
    this.playback1_1Target.hidden = rate !== 1.1;
    this.playback1_5Target.hidden = rate !== 1.5;
    this.playback2Target.hidden = rate !== 2;
  }

  seek(event) {
    this.getMiniPlayer().seek((event.currentTarget.value / 100) * this.durationValue);
  }

  show() {
    this.hideableTargets.forEach(target => target.classList.remove("opacity-0"));
  }

  hide() {
    this.hideableTargets.forEach(target => target.classList.add("opacity-0"));
  }

  updateProgress(event) {
    if (!this.hasProgressBarTarget) {
      return;
    }

    this.progressBarTarget.value = (event.detail.currentTime / event.detail.duration) * 100;
    this.currentTimeTarget.textContent = this.formatTime(event.detail.currentTime);
    this.durationTarget.textContent = this.formatTime(event.detail.duration);
  }

  getMiniPlayer() {
    return this.application.getControllerForElementAndIdentifier(
      document.querySelector("[data-audio-player]"),
      "media--audio-player"
    );
  }

  formatTime(time) {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }
}